import React from 'react';
import { Field, Radio, List, ListItem, Text } from '@groupelacentrale/contact-storybook';
import FieldSearch from './FieldSearch';
import './OneToMany.scss';
import { keypressCallback } from 'utils';

interface Props {
  name: string;
  label?: string | React.ReactNode;
  asideComponent?: React.ReactNode;
  value?: string;
  placeholder?: string;
  options?: string[];
  eligibleOptions?: string[];
  translatedOptions?: Map<string, string>;
  isDropdownMode?: boolean;
  forceList?: boolean;
  onSelect: (value: string) => void;
  errorMessage?: string;
}

const OneToManyField: React.ForwardRefRenderFunction<HTMLDivElement, Props> = (
  {
    name,
    label,
    asideComponent,
    value,
    placeholder,
    options,
    eligibleOptions,
    translatedOptions,
    isDropdownMode,
    forceList,
    onSelect,
    errorMessage,
    ...fieldProps
  },
  ref,
) => {
  const handleKeyPress = keypressCallback(onSelect, 'data-option');

  if (!options) {
    return null;
  }
  if (options.length === 0) {
    return (
      <Field
        ref={ref}
        label={label}
        name={name}
        value={translatedOptions?.get(value ?? '') ?? value ?? ''}
        errorMessage={errorMessage}
        disabled
        readOnly
      />
    );
  }
  if (options.length === 1) {
    return (
      <div ref={ref}>
        <Field
          label={label}
          name={name}
          value={translatedOptions?.get(options[0]) ?? options[0]}
          errorMessage={errorMessage}
          disabled
          readOnly
        />
      </div>
    );
  }
  if (options.length > 1 && options.length <= 6 && !forceList) {
    return (
      <div className="one-to-many field-wrapper" ref={ref}>
        <div className="label-wrapper">
          {label && <Text className="field-label">{label}</Text>}
          {asideComponent}
        </div>
        <List>
          {options.map((option: string) => (
            <ListItem
              key={option}
              style={eligibleOptions && !eligibleOptions.includes(option) ? { opacity: '0.5' } : undefined}
              tabIndex={0}
              data-option={option}
              onKeyPress={handleKeyPress}
            >
              <Radio
                name={name}
                theme="primary"
                value={option}
                label={translatedOptions?.get(option) ?? option}
                onChange={() => onSelect(option)}
                checked={option === value}
              />
            </ListItem>
          ))}
        </List>
        {errorMessage && (
          <div id={`formErrorMessage-${name}`} className="formErrorMessage">
            {errorMessage}
          </div>
        )}
      </div>
    );
  }
  return (
    <FieldSearch
      ref={ref}
      name={name}
      label={label}
      value={value}
      placeholder={placeholder}
      onSelect={onSelect}
      isDropdownMode={isDropdownMode}
      options={options}
      eligibleOptions={eligibleOptions}
      errorMessage={errorMessage}
      {...fieldProps}
    />
  );
};

const OneToMany = React.forwardRef(OneToManyField);

export default OneToMany;
